import React, { useCallback } from 'react';

import { AssociatedPurchaseOrdersField } from 'components/logistics/components/crossDockPurchaseOrders/create/AssociatedPurchaseOrdersField';
import { CrossDockPurchaseOrderServiceFeeSection } from 'components/logistics/components/crossDockPurchaseOrders/create/CrossDockPurchaseOrderServiceFeeSection';
import { CrossDockPicker } from 'components/procurement/components/purchaseOrdersV2/formComponents/CrossDockPicker';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';
import { FacilityPickerV2 } from 'shared/components/FacilityPickerV2';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * @param {Object} props
 * @param {Object} props.draft
 * @param {(updater: (prev: any) => any) => void} props.onDraftChange
 * @param {boolean} [props.showValidation]
 * @param {string[]} [props.primaryErrors]
 * @param {Object} [props.primaryFieldErrors]
 * @param {Array<{ chargeType: string, label: string, required: boolean }>} [props.additionalFeeTypes]
 * @param {Object} [props.feeFieldErrors]
 */
export function CrossDockPurchaseOrderCreatePrimaryCard({
    additionalFeeTypes = [],
    draft,
    feeFieldErrors = {},
    onDraftChange,
    primaryErrors = [],
    primaryFieldErrors = {},
    showValidation = false,
}) {
    const setDraftField = useCallback(
        (field, value) => {
            onDraftChange((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        [onDraftChange],
    );

    const handleInputChange = useCallback(
        (field) => (event) => {
            const value = event?.target?.value ?? '';
            setDraftField(field, value);
            if (field === 'scheduledDeliveryDate' && value && !draft.scheduledDeliveryTime) {
                setDraftField('scheduledDeliveryTime', '00:00');
            }
        },
        [draft.scheduledDeliveryTime, setDraftField],
    );

    const showErrors = showValidation === true;
    const getFieldError = useCallback(
        (field) => {
            if (!showErrors) return '';
            return primaryFieldErrors?.[field] ?? '';
        },
        [primaryFieldErrors, showErrors],
    );
    const renderFieldError = useCallback(
        (field) => {
            const message = getFieldError(field);
            if (!message) return null;
            return <p className="text-xs text-destructive">{message}</p>;
        },
        [getFieldError],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Cross Dock Purchase Order</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {showErrors && primaryErrors.length > 0 ? (
                    <div className="pb-6 pt-6">
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following to save</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {primaryErrors.map((message) => (
                                        <li key={message}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 pt-6 sm:grid-cols-2 xl:grid-cols-12">
                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <CrossDockPicker
                            value={draft.crossDockId}
                            onValueChange={(id) => setDraftField('crossDockId', id)}
                            label={
                                <>
                                    Destination Cross Dock <span className="text-destructive">*</span>
                                </>
                            }
                        />
                        {renderFieldError('crossDockId')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <FacilityPickerV2
                            label={
                                <>
                                    Facility <span className="text-destructive">*</span>
                                </>
                            }
                            value={draft.facilityId}
                            onValueChange={(facility) => setDraftField('facilityId', facility?.id ?? '')}
                        />
                        {renderFieldError('facilityId')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <Label htmlFor="dockDoor" className={labelClassName}>
                            Dock Door
                        </Label>
                        <Input
                            id="dockDoor"
                            name="dockDoor"
                            value={draft.dockDoor}
                            onChange={handleInputChange('dockDoor')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <Label htmlFor="scheduledDeliveryDate" className={labelClassName}>
                            Scheduled Delivery Date
                        </Label>
                        <Input
                            id="scheduledDeliveryDate"
                            name="scheduledDeliveryDate"
                            type="date"
                            value={draft.scheduledDeliveryDate}
                            onChange={handleInputChange('scheduledDeliveryDate')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <Label htmlFor="scheduledDeliveryTime" className={labelClassName}>
                            Scheduled Delivery Time
                        </Label>
                        <Input
                            id="scheduledDeliveryTime"
                            name="scheduledDeliveryTime"
                            type="time"
                            value={draft.scheduledDeliveryTime}
                            onChange={handleInputChange('scheduledDeliveryTime')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-3">
                        <Label htmlFor="shipDate" className={labelClassName}>
                            Ship Date
                        </Label>
                        <Input
                            id="shipDate"
                            name="shipDate"
                            type="date"
                            value={draft.shipDate}
                            onChange={handleInputChange('shipDate')}
                        />
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <Label htmlFor="minTemp" className={labelClassName}>
                            Min Temperature
                        </Label>
                        <Input
                            id="minTemp"
                            name="minTemp"
                            type="number"
                            inputMode="numeric"
                            value={draft.minTemp}
                            onChange={handleInputChange('minTemp')}
                        />
                        {renderFieldError('minTemp')}
                    </div>

                    <div className="flex flex-col gap-2 xl:col-span-2">
                        <Label htmlFor="maxTemp" className={labelClassName}>
                            Max Temperature
                        </Label>
                        <Input
                            id="maxTemp"
                            name="maxTemp"
                            type="number"
                            inputMode="numeric"
                            value={draft.maxTemp}
                            onChange={handleInputChange('maxTemp')}
                        />
                        {renderFieldError('maxTemp')}
                    </div>

                    <div className="xl:col-span-12">
                        <CrossDockPurchaseOrderServiceFeeSection
                            draft={draft}
                            onDraftChange={onDraftChange}
                            additionalFeeTypes={additionalFeeTypes}
                            showValidation={showValidation}
                            feeFieldErrors={feeFieldErrors}
                        />
                    </div>

                    <AssociatedPurchaseOrdersField
                        poMaps={draft.poMaps}
                        onPoMapsChange={(poMaps) => setDraftField('poMaps', poMaps)}
                        showValidation={showValidation}
                        errorMessage={getFieldError('poMaps')}
                    />

                    <div className="flex flex-col gap-2 xl:col-span-12">
                        <Label htmlFor="specialInstructions" className={labelClassName}>
                            Special Instructions
                        </Label>
                        <Textarea
                            id="specialInstructions"
                            name="specialInstructions"
                            value={draft.specialInstructions}
                            onChange={handleInputChange('specialInstructions')}
                            rows={3}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

CrossDockPurchaseOrderCreatePrimaryCard.displayName = 'CrossDockPurchaseOrderCreatePrimaryCard';
